/**
 * RecoveryKeyModal - Shows recovery key at signup
 *
 * User must confirm they've saved the key before continuing.
 * This is the only way to recover encrypted data if password is forgotten.
 */
import { memo, useState, useCallback } from 'react';
import { toast } from 'sonner';

interface RecoveryKeyModalProps {
  recoveryKey: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function RecoveryKeyModal({ recoveryKey, onConfirm, onCancel }: RecoveryKeyModalProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(recoveryKey);
      setCopied(true);
      toast.success('Recovery key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  }, [recoveryKey]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-bg-overlay border border-border-default rounded-lg p-spacing-6 max-w-md w-full mx-spacing-4 shadow-elevation-3">
        {/* Header */}
        <h2 className="text-dls-h2 font-normal text-primary mb-spacing-2">
          Save Your Recovery Key
        </h2>
        <p className="text-dls-body text-secondary mb-spacing-4">
          Write this down and store it somewhere safe. If you forget your password,
          this is the <strong className="text-primary">only way</strong> to recover your encrypted data.
        </p>

        {/* Recovery Key Display */}
        <div className="bg-bg-well border border-border-default rounded-lg p-spacing-4 mb-spacing-4">
          <div className="font-mono text-lg text-primary text-center tracking-wider select-all break-all">
            {recoveryKey}
          </div>
        </div>

        {/* Copy Button */}
        <button
          type="button"
          onClick={handleCopy}
          className="w-full h-10 bg-bg-panel border border-border-default rounded
                     hover:bg-bg-hover transition-colors duration-200
                     text-dls-body text-primary flex items-center justify-center gap-spacing-2 mb-spacing-4"
        >
          {copied ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy to clipboard
            </>
          )}
        </button>

        {/* Warning */}
        <div className="bg-warning/10 border border-warning/30 rounded-lg p-spacing-3 mb-spacing-4">
          <p className="text-dls-small text-warning">
            <strong>Warning:</strong> Without this recovery key, if you forget your password,
            all your encrypted conversations will be permanently lost.
          </p>
        </div>

        {/* Confirmation Checkbox */}
        <label className="flex items-start gap-spacing-3 mb-spacing-4 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-border-default bg-bg-input
                       text-accent focus:ring-accent focus:ring-offset-0"
          />
          <span className="text-dls-body text-primary">
            I have saved my recovery key in a secure location
          </span>
        </label>

        {/* Actions */}
        <div className="flex gap-spacing-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-10 bg-bg-panel border border-border-default rounded
                       hover:bg-bg-hover transition-colors duration-200 text-dls-body text-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!confirmed}
            className="flex-1 h-10 bg-accent rounded hover:bg-hover
                       transition-colors duration-200 text-dls-body text-tertiary
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(RecoveryKeyModal);
